import type { addToCartArgsType } from 'components/shared/Cart/types';
import { ProductType } from 'components/shared/Cart/types';
import type { AffiliateDetails } from 'utils/affiliateHelpers';
import {
  ShopifyStore,
  getEffectiveStoreForDiscount,
} from 'lib/commerce/shopify/shopifyHelper';
import type { PerfLabDiscount } from './types';

export const NO_PERF_LAB_DISCOUNT: PerfLabDiscount = {
  type: 'none',
  percentage: 0,
  discountCode: '',
  label: '',
};

export type PerformanceLabCartLine = {
  id: string;
  quantity: number;
  attributes?: Array<{ key: string; value: string }>;
};

type CartLineEdge = {
  node?: {
    quantity: number;
    attributes?: Array<{ key: string; value: string }>;
  };
};

export const getPerformanceLabCartLines = (
  cart: Record<string, unknown> | null,
): CartLineEdge[] => {
  if (!cart?.lines) return [];

  const edges = (cart.lines as { edges?: CartLineEdge[] }).edges;
  if (!edges) return [];

  return edges.filter((edge) =>
    edge.node?.attributes?.some(
      (attr) =>
        attr.key === '_product_type' &&
        attr.value === ProductType.PERFORMANCE_LAB,
    ),
  );
};

export const getPerformanceLabCartItemCount = (
  cart: Record<string, unknown> | null,
): number =>
  getPerformanceLabCartLines(cart).reduce(
    (sum, edge) => sum + (edge.node?.quantity ?? 0),
    0,
  );

export const findCartLine = (
  cart: Record<string, unknown> | null,
  shopifyVariantId?: string,
): PerformanceLabCartLine | null => {
  if (!cart?.lines || !shopifyVariantId) return null;

  const edges = (cart.lines as { edges?: Array<{ node?: PerformanceLabCartLine & { merchandise?: { id?: string } } }> })
    .edges;

  if (!edges) return null;

  const gid = `gid://shopify/ProductVariant/${shopifyVariantId}`;
  const edge = edges.find((e) => e.node?.merchandise?.id === gid);
  return edge?.node ?? null;
};

export const buildPerformanceLabCartLineItem = (
  rawVariantId: string,
  discountCode?: string,
): addToCartArgsType => ({
  shopifyVariantId: `gid://shopify/ProductVariant/${rawVariantId}`,
  productType: ProductType.PERFORMANCE_LAB,
  discountCode: discountCode || undefined,
});

/**
 * Resolve the affiliate discount for one Performance Labs plan.
 *
 * Performance Labs is a catalogue, not a single SKU, so ops can scope an
 * affiliate code to specific products. `shopifyVariantId` is what identifies
 * the plan on both sides — the backend publishes discounts keyed by it, and
 * `plans.ts` carries it on every plan.
 *
 * Resolution order is per-product override, then a catalogue-wide default.
 * A plan with no entry and no default is NOT discounted: previously this fell
 * back to the affiliate's RING discount, which silently applied a ring
 * promotion's percentage to diagnostics packages. Performance Labs now needs
 * its own configuration to discount anything.
 *
 * A plan with NO `shopifyVariantId` is never discounted. That id is what makes a
 * plan buyable through the cart, and the cart is the only path that attaches the
 * discount code — so a plan without one (a GLP program, which signs up through a
 * Typeform; a sold-out card) would otherwise display a struck-through price that
 * nothing downstream could ever honour. Shown price must equal charged price.
 */
export const resolvePerformanceLabAffiliateDiscount = (
  affiliateCode: string | undefined,
  affiliateDetails: AffiliateDetails | null | undefined,
  shopifyVariantId?: string,
): PerfLabDiscount => {
  if (!affiliateCode || !affiliateDetails) return NO_PERF_LAB_DISCOUNT;
  if (!affiliateDetails.performanceLabsActive) return NO_PERF_LAB_DISCOUNT;
  if (!shopifyVariantId) return NO_PERF_LAB_DISCOUNT;

  const store =
    getEffectiveStoreForDiscount(ShopifyStore.IN) ?? ShopifyStore.IN;
  const plConfig = affiliateDetails.performance_labs_discount_configs?.[store];
  if (!plConfig) return NO_PERF_LAB_DISCOUNT;

  const config = plConfig.products?.[shopifyVariantId] ?? plConfig.default;
  if (!config) return NO_PERF_LAB_DISCOUNT;

  const pct = config.type === 'percentage' ? Number(config.value) : 0;
  if (!Number.isFinite(pct) || pct <= 0) return NO_PERF_LAB_DISCOUNT;

  return {
    type: 'affiliate',
    percentage: pct,
    discountCode: config.code,
    label: `${pct}% OFF APPLIED`,
  };
};
